import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchPublicCmsData } from "../api/cmsApi";
import { getBlogBySlug, listBlogCategories, listBlogs } from "../data/cmsStore";

const base = process.env.PUBLIC_URL || "";

function renderContent(content) {
  return String(content || "")
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line, i, arr) => !(line === "" && arr[i - 1] === ""))
    .map((line, idx) => {
      if (line.startsWith("## ")) {
        return <h2 key={`h-${idx}`}>{line.replace(/^##\s+/, "")}</h2>;
      }
      if (line.startsWith("- ")) {
        return (
          <li key={`li-${idx}`} className="cms-article-li">
            {line.replace(/^-+\s+/, "")}
          </li>
        );
      }
      if (!line.trim()) return null;
      return <p key={`p-${idx}`}>{line}</p>;
    });
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blogs, setBlogs] = useState(() => listBlogs());
  const [categories, setCategories] = useState(() => listBlogCategories());
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchPublicCmsData();
        if (Array.isArray(data.blogs) && data.blogs.length) setBlogs(data.blogs);
        if (Array.isArray(data.blogCategories) && data.blogCategories.length) {
          setCategories(data.blogCategories);
        }
      } catch (_) {
        // Keep local fallback data if API is unreachable.
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  const blog = useMemo(
    () => blogs.find((b) => b.slug === slug) || getBlogBySlug(slug),
    [blogs, slug]
  );
  const related = useMemo(
    () => blogs.filter((b) => b.status === "published" && b.slug !== slug).slice(0, 2),
    [blogs, slug]
  );

  if (loading && !blog) {
    return (
      <div className="cms-public-page container py-5">
        <h1>Loading blog...</h1>
      </div>
    );
  }

  if (!blog || blog.status !== "published") {
    return (
      <div className="cms-public-page container py-5">
        <h1>Blog not found</h1>
        <Link to="/blog">Back to blogs</Link>
      </div>
    );
  }

  const cat = categories.find((c) => c.id === blog.categoryId)?.name;
  const tags = String(blog.tags || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <div className="cms-public-page">
      <article className="cms-article animate-on-scroll">
        <div className="container cms-article-inner">
          <Link to="/blog" className="cms-back">← All blogs</Link>
          {cat ? <span className="cms-chip">{cat}</span> : null}
          <h1>{blog.title}</h1>
          <p className="cms-article-lead">{blog.excerpt}</p>

          <div className="cms-info-grid cms-info-grid--blog">
            <div className="cms-info-card">
              <span>Author</span>
              <strong>{blog.author}</strong>
            </div>
            <div className="cms-info-card">
              <span>Published</span>
              <strong>{blog.publishedAt}</strong>
            </div>
            <div className="cms-info-card">
              <span>Reading time</span>
              <strong>{blog.readTime || "5 min read"}</strong>
            </div>
            {cat ? (
              <div className="cms-info-card">
                <span>Category</span>
                <strong>{cat}</strong>
              </div>
            ) : null}
          </div>

          <img className="cms-article-cover" src={`${base}${blog.image}`} alt="" />

          <div className="cms-article-body">{renderContent(blog.content)}</div>

          {tags.length ? (
            <div className="cms-tag-row">
              {tags.map((tag) => (
                <span key={tag} className="cms-tag">{tag}</span>
              ))}
            </div>
          ) : null}

          {related.length ? (
            <div className="cms-related">
              <h2>Related reading</h2>
              <div className="cms-related-grid">
                {related.map((item) => (
                  <Link key={item.id} to={`/blog/${item.slug}`} className="cms-related-card">
                    <span className="cms-meta">{item.publishedAt}</span>
                    <strong>{item.title}</strong>
                    <p>{item.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
}
